import { ROUTE_BRANCH_POINT_TERMINAL_UID, createTUID, Orientation, orientationOfVector, } from "@buildwithflux/models";
import { brandedEntries, brandedKeys, guid, compareFloats, compareVec2, roundFloatError, roundVec2FloatError, } from "@buildwithflux/shared";
import { find, isEqual, minBy, uniq, uniqBy } from "lodash";
import { seg2, vec2 } from "../common";
import { devAssert } from "../helpers";
// For brevity, since we use these values a lot:
const { HORIZONTAL, VERTICAL } = Orientation;
/* Note: most of the private functions below are design specifically to only work
 * with perfectly-perpendicular, axis-aligned, float-rounded segments and vertices.
 * normalizeConnectedWires() explicitly ensures this before calling them.
 * In other words, don't export these for use elsewhere.
 */
function segmentOrientation(segment) {
    return orientationOfVector(vec2.sub(segment[1], segment[0]));
}
function orientationInfo(segment) {
    const orientation = segmentOrientation(segment);
    const alignedAxis = (orientation === HORIZONTAL ? "x" : "y");
    const perpendicularAxis = (orientation === HORIZONTAL ? "y" : "x");
    return { orientation, alignedAxis, perpendicularAxis };
}
// TODO can we replace this with the more general seg2:overlap()? With care if so,
// because this does some specific things that are important for normalization.
function segmentsOverlap(s1, s2) {
    // Note: this function makes a bunch of assumptions. Eg segments are axis-aligned,
    // s1 is non-zero-length, and endpoints have been float-rounded. Hence this func is private.
    const { alignedAxis, perpendicularAxis } = orientationInfo([s1[0], s1[1]]);
    const sameLine = s1[0][perpendicularAxis] === s2[0][perpendicularAxis] &&
        // Ensure s2 is either same orientation or 0-length:
        s2[0][perpendicularAxis] === s2[1][perpendicularAxis];
    if (!sameLine)
        return false;
    const s1Ends = [s1[0][alignedAxis], s1[1][alignedAxis]];
    const s2Ends = [s2[0][alignedAxis], s2[1][alignedAxis]];
    // Ensure endpoints are in increasing order for correct inside/outside comparisons below.
    s1Ends.sort((a, b) => a - b);
    s2Ends.sort((a, b) => a - b);
    // >= <= (not > <) is important here so as to include segments that are exactly
    // touching because they're connected to the same vertex.
    // Otherwise we'd need another step to check .connectedVertices of segments.
    return s1Ends[0] <= s2Ends[1] && s2Ends[0] <= s1Ends[1];
}
function isBetween(n, range) {
    range.sort((a, b) => a - b);
    // >= <= is important for exact endpoint matching.
    return n >= range[0] && n <= range[1];
}
function orthogonalIntersect(s1, s2) {
    // Simplified segment intersection that only works if the lines are axis-aligned and perpendicular
    const orientations = [segmentOrientation(s1), segmentOrientation(s2)];
    if (orientations[0] === orientations[1])
        return undefined;
    if (orientations[0] === VERTICAL) {
        [s1, s2] = [s2, s1];
    }
    if (isBetween(s2[0].x, [s1[0].x, s1[1].x]) && isBetween(s1[0].y, [s2[0].y, s2[1].y])) {
        return { x: s2[0].x, y: s1[0].y };
    }
    return undefined;
}
function getOverlappingSegments(segment, allSegments, _processed = new Set()) {
    _processed.add(makeSegmentId(segment));
    const overlapping = allSegments.filter((s) => segmentsOverlap(segment, s));
    const allOverlapping = [segment];
    for (const other of overlapping) {
        if (_processed.has(makeSegmentId(other)))
            continue;
        allOverlapping.push(...getOverlappingSegments(other, allSegments, _processed));
    }
    return allOverlapping;
}
function makeSegmentId(segment) {
    // Use the positions for the segment id, because identically-positioned segments make no difference to the logic.
    return [`${segment[0].x}:${segment[0].y}`, `${segment[1].x}:${segment[1].y}`].sort().join(":");
}
/**
 * Given a vertex, normalize all the wires in the same net (but not across portals).
 *
 * NOTE: intended to be called via Wiring.normalizeConnectedWires(). Only exists here to
 * keep it in its own file because it's a large function.
 */
export function normalizeConnectedWires(wiringManager, vertexId, logger) {
    // TODO Maybe combine multiple vertices that point to the same terminal for any terminals of this net?
    // -> with care, because I think that'd require traversing all vertices, and break our perf-safety-boundary of
    // just traversing the net vertices.
    const existingVertexIds = wiringManager.getAllNetVerticesAcrossPortals(vertexId);
    const existingVertices = existingVertexIds.map((uid) => wiringManager.vertices.get(uid)).filter((v) => !!v);
    // Turn vertices into segments:
    const segmentMap = {};
    for (const vertex of existingVertices) {
        // For each connected vertex, create a segment between it and this vertex.
        // If there are no connections, convert it to a zero-length segment, so as
        // to preserve isolated vertices. We do this because in certain rare-ish
        // cases, singletons exist as an intermediate step in rewiring (for eg when
        // autoplacement deletes a wire's internal vertices to recreate it from
        // endpoint vertices)
        const connectedVertices = Object.keys(vertex.connectedVertices).length
            ? brandedKeys(vertex.connectedVertices)
                .map((c) => wiringManager.vertices.get(c))
                .filter((c) => !!c)
            : [vertex];
        for (const other of connectedVertices) {
            const segment = [
                // Round off float error when creating segments, to avoid subtle
                // float inequality issues later in the code. Should-be-equal-but-not
                // numbers easily get assigned to vertex.position by various UI code,
                // and part of the job of normalization is to clean those up.
                roundVec2FloatError(vertex.position),
                roundVec2FloatError(other.position),
            ];
            const segmentId = makeSegmentId(segment);
            if (segmentId in segmentMap)
                continue;
            segmentMap[segmentId] = segment;
        }
    }
    // Filter out zero-length segments and add them back in later, because they make
    // the merge logic more complicated, and we can just merge them in after
    const segments = Object.values(segmentMap).filter((s) => seg2.len(s));
    const zeroSegments = Object.values(segmentMap).filter((s) => !seg2.len(s));
    const terminalVertices = existingVertices.filter((v) => v.terminal && v.terminal.terminal_uid !== ROUTE_BRANCH_POINT_TERMINAL_UID);
    // Merge multiple overlapping and connected segments into single segments:
    const processedSegments = new Set();
    const mergedSegments = [];
    for (const segment of segments) {
        if (processedSegments.has(makeSegmentId(segment)))
            continue;
        const overlapping = getOverlappingSegments(segment, segments);
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        overlapping.forEach((s) => processedSegments.add(makeSegmentId(s)));
        const { orientation, alignedAxis, perpendicularAxis } = orientationInfo([overlapping[0][0], overlapping[0][1]]);
        const pointsOnSegment = overlapping.flatMap((s) => [s[0][alignedAxis], s[1][alignedAxis]]);
        let ends = [
            { x: Math.min(...pointsOnSegment), y: segment[0][perpendicularAxis] },
            { x: Math.max(...pointsOnSegment), y: segment[0][perpendicularAxis] },
        ];
        if (orientation === VERTICAL) {
            ends = ends.map((end) => ({ x: end.y, y: end.x }));
        }
        mergedSegments.push(ends);
    }
    // Add back in zero-length segments that aren't already spanned by nonzero segments (needed for
    // co-located terminals and for preserving isolated vertices, per earlier comment):
    mergedSegments.push(...zeroSegments.filter((z) => !mergedSegments.some((s2) => segmentsOverlap(s2, z))));
    // Next, split merged segments at the points that are actually joined to other segments,
    // and add vertices at the correct points (intersections, terminals, endpoints).
    const normalizedVertices = {};
    function vertexAt(point) {
        // Vertex positions aren't (necessarily) pre-rounded below, unlike the segments
        // above, so explicitly round to avoid bad float comparisons.
        // TODO PERF vertexAt() and these n^2 lookups are the major perf bottleneck of normalization,
        // which is starting to get slow on big nets with a lot of portals (eg ground!).
        // Solve it by indexing them by (float-rounded) position.
        let vertex = find(normalizedVertices, (v) => compareVec2(v.position, point));
        if (!vertex) {
            vertex = existingVertices.find((v) => compareVec2(v.position, point));
            if (vertex) {
                vertex.connectedVertices = {};
            }
            else {
                vertex = { position: { ...point }, id: guid(), connectedVertices: {}, terminal: null };
            }
            normalizedVertices[vertex.id] = vertex;
        }
        return vertex;
    }
    for (const segment of mergedSegments) {
        // TODO some ~O(N^2) loops in here we may need to consider factoring out. It's not terrible
        // though because N is only the size of a single net, i.e. small compared to the total number
        // of wires in a project. Also we only run this code on individual actions, not eg during render.
        const joinPoints = mergedSegments.map((s) => orthogonalIntersect(segment, s)).filter((p) => !!p);
        const { orientation, alignedAxis, perpendicularAxis } = orientationInfo(segment);
        const terminalPositions = terminalVertices
            .map((t) => t.position)
            .filter((p) => compareFloats(p[perpendicularAxis], segment[0][perpendicularAxis]) &&
            isBetween(p[alignedAxis], [segment[0][alignedAxis], segment[1][alignedAxis]]));
        joinPoints.push(...terminalPositions);
        joinPoints.push(...segment);
        joinPoints.sort((a, b) => a[alignedAxis] - b[alignedAxis]);
        let prev = undefined;
        for (const point of uniqBy(joinPoints, (p) => roundFloatError(p[alignedAxis]))) {
            const vertex = vertexAt(point);
            if (prev) {
                prev.connectedVertices[vertex.id] = orientation;
                vertex.connectedVertices[prev.id] = orientation;
            }
            prev = vertex;
        }
    }
    // Ensure all terminals connected to the previous wires are connected to the new set of wires:
    if (mergedSegments.length || terminalVertices.length > 1) {
        const missingTerminalVertices = terminalVertices.filter((existing) => !find(normalizedVertices, (v) => isEqual(existing.terminal, v.terminal)));
        for (const missing of missingTerminalVertices) {
            let colocatedVertex = find(normalizedVertices, (v) => compareVec2(v.position, missing.position));
            if (!colocatedVertex) {
                // If we get here, normalization failed on at least one terminal, possibly due to
                // diagonal wires due to bugs elsewhere. Handle gracefully, because arguably this
                // layer of code shouldn't have opinions on diagonal wires, and also because it's
                // the best way in the current structure to avoid further corruption and user pain.
                devAssert(false, `Wire normalization didn't create a vertex at terminal ${createTUID(missing.terminal)}`, logger);
                const connection = brandedEntries(missing.connectedVertices)[0];
                missing.connectedVertices = {};
                if (connection) {
                    const connected = normalizedVertices[connection[0]] ??
                        minBy(Object.values(normalizedVertices), (v) => vec2.distance(v.position, missing.position));
                    if (!connected) {
                        throw new Error("Empty normalizedVertices list. Should be impossible since mergedSegments.length > 0");
                    }
                    missing.connectedVertices[connected.id] = connection[1];
                    connected.connectedVertices[missing.id] = connection[1];
                }
                normalizedVertices[missing.id] = missing;
                continue;
            }
            if (colocatedVertex.terminal) {
                // If there's already a terminal attached to that vertex, add original vertex at the
                // same position (each terminal needs its own vertex). This will handle any
                // zero-length segments in the net that connect co-located terminals.
                missing.connectedVertices = {};
                colocatedVertex.connectedVertices[missing.id] = HORIZONTAL;
                missing.connectedVertices[colocatedVertex.id] = HORIZONTAL;
                normalizedVertices[missing.id] = missing;
                colocatedVertex = missing;
            }
            colocatedVertex.terminal = missing.terminal;
        }
    }
    // Persist to the class's vertex map:
    const toDelete = existingVertexIds.filter((uid) => !(uid in normalizedVertices));
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    toDelete.forEach((uid) => wiringManager.vertices.delete(uid));
    for (const vertex of Object.values(normalizedVertices)) {
        wiringManager.vertices.set(vertex.id, vertex);
    }
    return uniq([...existingVertexIds, ...brandedKeys(normalizedVertices), ...toDelete]);
}
//# sourceMappingURL=WiringNormalization.js.map